/**
 * What kind of virtual tour a listing carries, from its URL.
 *
 * WHY THIS IS WORTH A MODULE
 *
 * 44% of active listings carry a `virtualTourUrl` (measured across 600 live
 * listings, 2026-08-14), and until now the web app did exactly one thing with
 * it: rendered a text link at the bottom of the detail page. Nothing on any
 * search card, the home feed, favorites or a city page hinted that nearly half
 * the homes on screen had a tour behind them — so the feature existed and was
 * undiscoverable, which is indistinguishable from not shipping it.
 *
 * The iOS app has classified these for a while (`GRCore/VirtualTour.swift`,
 * Matterport / YouTube / iGUIDE / generic). This is that idea on the web, and
 * the two lists are deliberately close so a buyer who uses both sees the same
 * words for the same thing.
 *
 * VIMEO IS NEW ON BOTH SIDES. It appeared 11 times in the live sample and
 * neither client recognised it, so those tours were labelled with the generic
 * fallback despite being ordinary video.
 *
 * MATCHED ON HOST, NEVER ON THE WHOLE URL. A path or query string can contain
 * anything — `?ref=youtube` on a Matterport link would misclassify a 3D tour as
 * a video — so only the hostname is consulted.
 */

export type TourKind = 'threeD' | 'video' | 'iguide' | 'generic';

export interface VirtualTour {
  url: string;
  kind: TourKind;
  /** What the badge says. Short: it sits on a photo corner. */
  label: string;
}

const LABELS: Record<TourKind, string> = {
  threeD: '3D tour',
  video: 'Video tour',
  iguide: 'iGUIDE tour',
  generic: 'Virtual tour',
};

function hostOf(raw: string): string | null {
  try {
    const url = new URL(raw);
    // http and https only. The feed is agent-entered free text and has carried
    // worse; a `javascript:` or `data:` URL must never reach an href.
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.hostname.toLowerCase();
  } catch {
    return null;
  }
}

function kindFor(host: string): TourKind {
  if (host.includes('matterport')) return 'threeD';
  if (host.includes('youtube') || host === 'youtu.be' || host.endsWith('.youtu.be')) return 'video';
  if (host.includes('vimeo')) return 'video';
  // youriguide.com, goiguide.com, iguide.io — all carry the substring.
  if (host.includes('iguide')) return 'iguide';
  return 'generic';
}

/**
 * Classify a raw tour URL. Returns null for anything missing, unparseable, or
 * not http(s), so callers can `if (tour)` to decide whether to render at all.
 */
export function classifyTour(raw: string | null | undefined): VirtualTour | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const host = hostOf(trimmed);
  if (!host) return null;
  const kind = kindFor(host);
  return { url: trimmed, kind, label: LABELS[kind] };
}
